/**
 * PH Restaurant POS - Sync Service v3
 * Handles background synchronization with retry logic and exponential backoff
 */
import { 
  syncQueue, 
  offlineTickets, 
  menuCache, 
  tablesCache,
  syncMeta,
  SyncStatus,
  SyncOperationType 
} from './offlineDb';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Retryable status codes (server errors, gateway errors)
const RETRYABLE_STATUS_CODES = new Set([502, 503, 504, 520]);

// Non-retryable status codes (client errors, logic errors)
const NON_RETRYABLE_STATUS_CODES = new Set([400, 401, 403, 409, 422]);

// Max retries for transient errors
const MAX_RETRIES = 3;

// Base delay for exponential backoff (ms)
const BASE_DELAY = 1000;

/**
 * Sleep helper
 */
const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Calculate exponential backoff delay
 */
const getBackoffDelay = (attempt) => {
  const delay = BASE_DELAY * Math.pow(2, attempt);
  const jitter = Math.random() * 500;
  return Math.min(delay + jitter, 30000); // Max 30 seconds
};

/**
 * Fetch with retry logic for transient errors
 */
async function fetchWithRetry(url, options, maxRetries = MAX_RETRIES) {
  let lastError;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30s timeout
      
      const response = await fetch(url, {
        ...options,
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      // Check if response is retryable
      if (RETRYABLE_STATUS_CODES.has(response.status)) {
        if (attempt < maxRetries) {
          const delay = getBackoffDelay(attempt);
          console.log(`Retryable error ${response.status}, retrying in ${delay}ms (attempt ${attempt + 1}/${maxRetries})`);
          await sleep(delay);
          continue;
        }
      }
      
      // Non-retryable error or success
      return response;
      
    } catch (error) {
      lastError = error;
      
      // Network errors are retryable
      if (error.name === 'AbortError' || error.name === 'TypeError') {
        if (attempt < maxRetries) {
          const delay = getBackoffDelay(attempt);
          console.log(`Network error, retrying in ${delay}ms (attempt ${attempt + 1}/${maxRetries})`);
          await sleep(delay);
          continue;
        }
      }
      
      throw error;
    }
  }
  
  throw lastError || new Error('Max retries exceeded');
}

/**
 * Sync Service singleton
 */
class SyncService {
  constructor() {
    this.isOnline = navigator.onLine;
    this.isSyncing = false;
    this.syncInterval = null;
    this.listeners = new Set();
    this.lastSyncError = null;
    this.menuEtag = null;
    this.tablesEtag = null;
    
    // Listen for online/offline events
    window.addEventListener('online', () => this.handleOnlineStatusChange(true));
    window.addEventListener('offline', () => this.handleOnlineStatusChange(false));
  }

  /**
   * Subscribe to sync events
   */
  subscribe(listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * Notify all listeners
   */
  notify(event, data) {
    this.listeners.forEach(listener => {
      try {
        listener({ type: event, ...data });
      } catch (e) {
        console.error('Sync listener error:', e);
      }
    });
  }

  /**
   * Handle online/offline status change
   */
  async handleOnlineStatusChange(isOnline) {
    this.isOnline = isOnline;
    await syncMeta.setOnlineStatus(isOnline);
    this.notify('STATUS_CHANGE', { isOnline });
    
    if (isOnline) {
      // Trigger sync when coming back online
      this.syncAll();
    }
  }

  /**
   * Start background sync (call on app init)
   */
  startBackgroundSync(intervalMs = 30000) {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
    }
    
    // Initial sync
    this.syncAll();
    
    // Periodic sync
    this.syncInterval = setInterval(() => {
      if (this.isOnline && !this.isSyncing) {
        this.syncAll();
      }
    }, intervalMs);
    
    console.log('Background sync started');
  }

  /**
   * Stop background sync
   */
  stopBackgroundSync() {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
    }
    console.log('Background sync stopped');
  }

  /**
   * Full sync - upload pending, download fresh data
   */
  async syncAll() {
    if (this.isSyncing || !this.isOnline) {
      return { success: false, reason: this.isSyncing ? 'already_syncing' : 'offline' };
    }

    this.isSyncing = true;
    this.notify('SYNC_START', {});
    
    try {
      // 1. Upload pending operations with retry
      const uploadResult = await this.uploadPendingOperations();
      
      // 2. Download fresh menu data (with ETag check)
      await this.downloadMenuData();
      
      // 3. Download tables data (with ETag check)
      await this.downloadTablesData();
      
      // 4. Update last sync time
      await syncMeta.setLastSyncTime();
      
      this.lastSyncError = null;
      this.notify('SYNC_COMPLETE', { uploadResult });
      
      return { success: true, uploadResult };
    } catch (error) {
      console.error('Sync error:', error);
      this.lastSyncError = error;
      this.notify('SYNC_ERROR', { error: error.message });
      return { success: false, error: error.message };
    } finally {
      this.isSyncing = false;
    }
  }

  /**
   * Upload pending operations to server with retry logic
   */
  async uploadPendingOperations() {
    const pendingOps = await syncQueue.getPendingOperations();
    
    if (pendingOps.length === 0) {
      return { synced: 0, failed: 0, total: 0 };
    }

    let synced = 0;
    let failed = 0;

    for (const op of pendingOps) {
      try {
        await syncQueue.markSyncing(op.id);
        const payload = JSON.parse(op.payload);
        
        await this.processOperationWithRetry(op.type, payload);
        
        await syncQueue.markSynced(op.id);
        synced++;
        
        this.notify('OPERATION_SYNCED', { operation: op.type, entityId: op.entityId });
      } catch (error) {
        console.error(`Failed to sync operation ${op.id}:`, error);
        
        // Check if error is non-retryable
        const statusCode = error.response?.status || error.status;
        if (NON_RETRYABLE_STATUS_CODES.has(statusCode)) {
          // Mark as permanently failed
          await syncQueue.markFailed(op.id, error, true);
        } else {
          await syncQueue.markFailed(op.id, error);
        }
        
        failed++;
        
        // Handle idempotency conflicts as success
        if (statusCode === 409 || error.message?.includes('already_synced')) {
          await syncQueue.markSynced(op.id);
          synced++;
          failed--;
        }
      }
    }

    // Clear old synced operations
    await syncQueue.clearSynced();

    return { synced, failed, total: pendingOps.length };
  }

  /**
   * Process operation with retry logic
   */
  async processOperationWithRetry(type, payload) {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('Not authenticated');

    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };

    switch (type) {
      case SyncOperationType.CREATE_TICKET:
        return await this.syncCreateTicket(payload, headers);
      
      case SyncOperationType.ADD_ITEM:
        return await this.syncAddItem(payload, headers);
      
      case SyncOperationType.VOID_ITEM:
        return await this.syncVoidItem(payload, headers);
      
      case SyncOperationType.PROCESS_PAYMENT:
        return await this.syncPayment(payload, headers);
      
      case SyncOperationType.CASH_IN:
      case SyncOperationType.CASH_OUT:
      case SyncOperationType.DEPOSIT:
        return await this.syncCashMovement(type, payload, headers);
      
      default:
        console.warn(`Unknown operation type: ${type}`);
        return { status: 'skipped', reason: 'unknown_operation' };
    }
  }

  /**
   * Sync create ticket with retry
   */
  async syncCreateTicket(ticket, headers) {
    const response = await fetchWithRetry(`${API_URL}/api/sync/tickets`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        offline_id: ticket.offlineId,
        idempotency_key: ticket.idempotencyKey,
        outlet_id: ticket.outletId,
        table_id: ticket.tableId,
        guest_count: ticket.guestCount,
        items: ticket.items || [],
        payments: ticket.payments || [],
        created_at: ticket.createdAt,
        total: ticket.total || 0
      })
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
      const err = new Error(error.detail || 'Failed to sync ticket');
      err.status = response.status;
      throw err;
    }

    const result = await response.json();
    
    if (result.ticket_id) {
      await offlineTickets.markSynced(ticket.id, result.ticket_id);
    }

    return result;
  }

  /**
   * Sync add item with retry
   */
  async syncAddItem(payload, headers) {
    const { ticketId, item } = payload;
    
    const ticket = await offlineTickets.getTicket(ticketId);
    if (!ticket?.serverTicketId) {
      throw new Error('Ticket not synced yet');
    }

    const response = await fetchWithRetry(`${API_URL}/api/pos/tickets/${ticket.serverTicketId}/items`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        menu_item_id: item.menuItemId,
        quantity: item.quantity,
        notes: item.notes
      })
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
      const err = new Error(error.detail || 'Failed to sync item');
      err.status = response.status;
      throw err;
    }

    return await response.json();
  }

  /**
   * Sync void item
   */
  async syncVoidItem(payload, headers) {
    // Void operations need server item ID mapping
    console.warn('Void item sync needs server item ID mapping');
    return { status: 'skipped', reason: 'needs_item_mapping' };
  }

  /**
   * Sync payment with retry
   */
  async syncPayment(payload, headers) {
    const { ticketId, payment } = payload;
    
    const ticket = await offlineTickets.getTicket(ticketId);
    if (!ticket?.serverTicketId) {
      throw new Error('Ticket not synced yet');
    }

    const response = await fetchWithRetry(`${API_URL}/api/pos/tickets/${ticket.serverTicketId}/pay`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        method: payment.method,
        amount: payment.amount,
        tendered: payment.tendered,
        idempotency_key: payment.idempotencyKey
      })
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
      const err = new Error(error.detail || 'Failed to sync payment');
      err.status = response.status;
      throw err;
    }

    return await response.json();
  }

  /**
   * Sync cash movement (requires manager PIN - skip for offline)
   */
  async syncCashMovement(type, movement, headers) {
    console.warn('Cash movement sync requires manager PIN - marking as needs_review');
    return { status: 'needs_review', reason: 'requires_manager_pin' };
  }

  /**
   * Download menu data with ETag support
   */
  async downloadMenuData() {
    try {
      const token = localStorage.getItem('token');
      const outletId = localStorage.getItem('selectedOutletId');
      
      if (!token || !outletId) return;

      const headers = { 'Authorization': `Bearer ${token}` };
      if (this.menuEtag) {
        headers['If-None-Match'] = `"${this.menuEtag}"`;
      }

      const response = await fetchWithRetry(`${API_URL}/api/sync/menu/${outletId}`, {
        headers
      });

      // 304 Not Modified - cache is still valid
      if (response.status === 304) {
        console.log('Menu cache still valid (ETag match)');
        return;
      }

      if (!response.ok) return;

      const data = await response.json();
      
      // Save ETag for future requests
      this.menuEtag = data.etag || response.headers.get('ETag')?.replace(/"/g, '');
      
      // Cache categories
      const categories = data.categories.map((cat, index) => ({
        id: cat.id,
        name: cat.name,
        icon: cat.icon,
        sortOrder: index,
        isActive: true
      }));
      await menuCache.cacheCategories(categories);

      // Cache menu items
      const items = data.categories.flatMap(cat => 
        cat.items.map(item => ({
          id: item.id,
          categoryId: cat.id,
          name: item.name,
          price: item.price,
          isTaxable: item.taxable,
          isActive: true
        }))
      );
      await menuCache.cacheMenuItems(items);

      this.notify('MENU_CACHED', { categories: categories.length, items: items.length });
    } catch (error) {
      console.error('Failed to download menu:', error);
    }
  }

  /**
   * Download tables data with ETag support
   */
  async downloadTablesData() {
    try {
      const token = localStorage.getItem('token');
      const outletId = localStorage.getItem('selectedOutletId');
      
      if (!token || !outletId) return;

      const headers = { 'Authorization': `Bearer ${token}` };
      if (this.tablesEtag) {
        headers['If-None-Match'] = `"${this.tablesEtag}"`;
      }

      const response = await fetchWithRetry(`${API_URL}/api/sync/tables/${outletId}`, {
        headers
      });

      // 304 Not Modified
      if (response.status === 304) {
        console.log('Tables cache still valid (ETag match)');
        return;
      }

      if (!response.ok) return;

      const data = await response.json();
      
      this.tablesEtag = data.etag || response.headers.get('ETag')?.replace(/"/g, '');
      
      await tablesCache.cacheTables(data.tables);

      this.notify('TABLES_CACHED', { count: data.tables.length });
    } catch (error) {
      console.error('Failed to download tables:', error);
    }
  }

  /**
   * Check if Z-lock is active
   */
  async checkZLock(outletId) {
    if (!this.isOnline) {
      const lastCheck = await syncMeta.get(`z_lock_${outletId}`);
      return lastCheck === 'true';
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetchWithRetry(`${API_URL}/api/cashier/day-status?outlet_id=${outletId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) return false;

      const data = await response.json();
      await syncMeta.set(`z_lock_${outletId}`, String(data.is_locked));
      return data.is_locked;
    } catch (error) {
      console.error('Failed to check Z-lock:', error);
      return false;
    }
  }

  /**
   * Get sync status
   */
  async getStatus() {
    const queueStats = await syncQueue.getQueueStats();
    const lastSync = await syncMeta.getLastSyncTime();
    
    return {
      isOnline: this.isOnline,
      isSyncing: this.isSyncing,
      lastSyncTime: lastSync,
      lastSyncError: this.lastSyncError?.message,
      queue: queueStats
    };
  }
}

// Export singleton instance
export const syncService = new SyncService();

export default syncService;
